import { forwardRef } from 'react';
import styled from '@emotion/styled';
import { type To, useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';

export const StyledAnchor = styled.a(({ theme }) => ({
  color: theme.palette.hyperlink,
  textDecoration: 'underline',
}));

export type LinkProps = {
  replace?: boolean;
  to: To;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

function resolveHref(to: To): string | undefined {
  if (typeof to === 'string') {
    const isExternal = /^https?:\/\//i.test(to);

    if (isExternal) {
      return to;
    }
    return undefined;
  }

  return undefined;
}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href, to, onClick, children, replace, ...otherProps }, ref) => {
    const navigate = useNavigate();
    const resolvedHref = href || resolveHref(to);

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      // Allow modifier clicks (cmd/ctrl/shift + click)
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey || event.button !== 0) {
        return;
      }

      onClick?.(event);

      // Let a consumer's onClick cancel the navigation (e.g. it handles the click itself).
      if (event.defaultPrevented) {
        return;
      }

      if (!resolvedHref) {
        event.preventDefault();
        navigate(to, { replace });
      }
    };

    return (
      <a
        ref={ref}
        {...otherProps}
        href={resolvedHref}
        onClick={handleClick}
        {...(resolvedHref ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    );
  }
);

export const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.hyperlink,
  cursor: 'pointer',
  textDecoration: 'underline',
}));
